import React, { useEffect, useState } from 'react';
import { Box, Typography, Stack, Chip, CircularProgress } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { storeReturnToForEvents } from '../../utils/navigationReturnTo';
import dayjs from 'dayjs';
import getChildOrders from '../../hooks/user/getChildOrders';
import { getClientId, getLocationId } from '../../utils';

interface ChildRegistrationsListProps {
  childId: number;
}

interface EventRegistration {
  eventId: number;
  eventUuid: string;
  eventName: string;
  startDate: string;
  endDate: string;
  status: 'Active' | 'Upcoming' | 'Completed';
}

const ChildRegistrationsList: React.FC<ChildRegistrationsListProps> = ({ childId }) => {
  const [registrations, setRegistrations] = useState<EventRegistration[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const events_api_url = import.meta.env.VITE_EVENTS_API_URL;
  const client_id = getClientId();
  const locationId = getLocationId();

  useEffect(() => {
    const fetchRegistrations = async () => {
      setLoading(true);
      try {
        // Step 1: Get event IDs (and order items if needed later) from /users endpoint
        const { eventIds } = await getChildOrders(childId);

        if (!eventIds || eventIds.length === 0) {
          setRegistrations([]);
          setLoading(false);
          return;
        }

        // Step 2: Fetch full event details from /events endpoint
        const eventIdsParam = eventIds.join(',');
        let url = `${events_api_url}/events?filter.id=$in:${eventIdsParam}&filter.status=1`;
        if (locationId) {
          url += `&filter.locationId=${locationId}`;
        }

        const response = await axios.get(url, {
          headers: { client_id: client_id },
        });

        const events = response.data?.data?.data || [];

        const today = dayjs();

        // Process events to create registrations
        const eventRegistrations: EventRegistration[] = events
          .map((event: any) => {
            const startDate = dayjs(event.startDate);
            const endDate = dayjs(event.endDate);
            
            // Determine event status
            let status: 'Active' | 'Upcoming' | 'Completed';
            if (endDate.isBefore(today, 'day')) {
              status = 'Completed';
            } else if (startDate.isAfter(today, 'day')) {
              status = 'Upcoming';
            } else {
              status = 'Active';
            }

            return {
              eventId: event.id,
              eventUuid: event.uuid,
              eventName: event.name,
              startDate: event.startDate,
              endDate: event.endDate,
              status,
            };
          })
          // de-dupe (defensive) in case API ever returns duplicates
          .filter((reg: EventRegistration, idx: number, arr: EventRegistration[]) => {
            const key = reg.eventUuid || String(reg.eventId);
            return arr.findIndex((r) => (r.eventUuid || String(r.eventId)) === key) === idx;
          })
          // Filter to show only Active and Upcoming
          .filter((reg: EventRegistration) => reg.status === 'Active' || reg.status === 'Upcoming')
          // Sort by start date (earliest first)
          .sort((a: EventRegistration, b: EventRegistration) => 
            dayjs(a.startDate).diff(dayjs(b.startDate))
          );

        setRegistrations(eventRegistrations);
      } catch (error) {
        console.error('❌ Error fetching registrations:', error);
        setRegistrations([]);
      } finally {
        setLoading(false);
      }
    };

    fetchRegistrations();
  }, [childId, events_api_url, client_id, locationId]);

  const handleEventClick = (eventUuid: string) => {
    storeReturnToForEvents();
    navigate(`/event/${eventUuid}`);
  };

  const formatDate = (dateString: string) => {
    return dayjs(dateString).format('MM/DD/YYYY');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Active':
        return 'success';
      case 'Upcoming':
        return 'primary';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (registrations.length === 0) {
    return (
      <Box sx={{ py: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          No upcoming events
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ py: 1 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: 'text.primary' }}>
        Upcoming Events:
      </Typography>
      <Stack spacing={1}>
        {registrations.map((registration) => (
          <Box
            key={registration.eventUuid || String(registration.eventId)}
            onClick={() => handleEventClick(registration.eventUuid)}
            sx={{
              p: 1.5,
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: 'action.hover',
                borderColor: 'primary.main',
                transform: 'translateX(4px)',
              },
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                <Chip
                  label={registration.status}
                  size="small"
                  color={getStatusColor(registration.status)}
                  sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600 }}
                />
              </Box>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.25 }} noWrap>
                {registration.eventName}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                {formatDate(registration.startDate)} - {formatDate(registration.endDate)}
              </Typography>
            </Box>
            <ArrowForwardIcon sx={{ fontSize: 16, color: 'action.active', flexShrink: 0 }} />
          </Box>
        ))}
      </Stack>
    </Box>
  );
};

export default ChildRegistrationsList;

